import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Q5_OPTIONS, Q6_OPTIONS, Q7_OPTIONS, Q8_OPTIONS, Q9_OPTIONS,
  Q_MINDSET_OPTIONS, Q10_OPTIONS, Q11_OPTIONS, Q12_OPTIONS,
  Q14_OPTIONS, Q15_OPTIONS, Q16_OPTIONS, Q18_OPTIONS,
  Q19_OPTIONS, Q20_OPTIONS, Q22_OPTIONS,
} from '../constants/surveyOptions';

const API = process.env.REACT_APP_API_URL || '';

// Extract GA4 client ID from the _ga cookie (format: GA1.1.<client_id>)
function getGaClientId() {
  try {
    const match = document.cookie.match(/_ga=GA\d+\.\d+\.(\d+\.\d+)/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

const STEPS = [
  { id: 1, label: 'About you & your car' },
  { id: 2, label: 'Maintenance experience' },
  { id: 3, label: 'Your behaviour' },
  { id: 4, label: 'What matters most' },
  { id: 5, label: 'A bit more about you' },
];


export default function Survey() {
  const pageLoadTime = useRef(Date.now());
  const utmRef = useRef({ utm_source: null, utm_medium: null, utm_campaign: null });
  const [step, setStep] = useState(1);
  const [exited, setExited] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    utmRef.current = {
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
    };
  }, []);

  const [form, setForm] = useState({
    _hp: '',  // honeypot — must stay empty
    q1: '', q2: '', q3: '', q_own: '', q4: '',
    q_mindset: '',
    q5: '', q6: '', q7: '', q8: '', q9: '',
    q10: [], q11: '', q12: '', q13: '',
    q14: [], q15: '', q16: '', q17: '', q18: [],
    q19: '', q20: '', q21: '', q22: '', q23: '',
  });

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setError('');
  }

  function toggleCheckbox(field, value, max) {
    setForm(f => {
      const curr = f[field];
      if (curr.includes(value)) return { ...f, [field]: curr.filter(v => v !== value) };
      if (max && curr.length >= max) {
        setError(`Please select at most ${max} options.`);
        return f;
      }
      setError('');
      return { ...f, [field]: [...curr, value] };
    });
  }

  function validate() {
    if (step === 1) {
      if (!form.q1) return 'Please answer whether you own a car.';
      if (!form.q2.trim()) return 'Please enter your city.';
      if (!form.q3.trim()) return 'Please enter your car details.';
      if (!form.q_own) return 'Please select how long you have owned the car.';
      if (!form.q4) return 'Please select your monthly km.';
    }
    if (step === 2) {
      if (!form.q_mindset) return 'Please tell us how you relate to your car.';
      if (!form.q5) return 'Please answer Q5.';
      if (!form.q6) return 'Please answer Q6.';
      if (!form.q7) return 'Please answer Q7.';
      if (!form.q8) return 'Please answer Q8.';
      if (!form.q9) return 'Please answer Q9.';
    }
    if (step === 3) {
      if (form.q10.length === 0) return 'Please select at least one option for Q10.';
      if (!form.q11) return 'Please answer Q11.';
      if (!form.q12) return 'Please answer Q12.';
    }
    if (step === 4) {
      if (form.q14.length === 0) return 'Please select what matters most to you.';
      if (!form.q15) return 'Please answer Q15.';
      if (!form.q16) return 'Please answer Q16.';
    }
    if (step === 5) {
      if (!form.q19) return 'Please select your age group.';
      if (!form.q20) return 'Please select your gender.';
      if (!form.q22) return 'Please select your education level.';
    }
    return null;
  }

  function next() {
    const err = validate();
    if (err) { setError(err); return; }

    if (step === 1 && form.q1 === 'no') { setExited(true); return; }
    if (step < STEPS.length) { setStep(s => s + 1); setError(''); window.scrollTo(0, 0); }
  }

  function back() {
    if (step > 1) { setStep(s => s - 1); setError(''); window.scrollTo(0, 0); }
  }

  async function submit() {
    const err = validate();
    if (err) { setError(err); return; }
    setSubmitting(true);
    try {
      const elapsed = Date.now() - pageLoadTime.current;
      const payload = {
        ...form,
        q10: JSON.stringify(form.q10),
        q14: JSON.stringify(form.q14),
        q18: JSON.stringify(form.q18),
        q_own: form.q_own,
        _elapsed: elapsed,
        ...utmRef.current,
        // device / session fingerprint
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        referrer: document.referrer || null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        // GA4 client ID — links this response to the GA session
        ga_client_id: getGaClientId(),
      };
      await fetch(`${API}/survey`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setSubmitted(true);
      window.scrollTo(0, 0);
      // Fire GA4 conversion event — ties survey completion to the GA session
      if (window.gtag) {
        window.gtag('event', 'survey_complete', {
          utm_source: utmRef.current.utm_source,
          utm_campaign: utmRef.current.utm_campaign,
        });
      }
    } catch {
      setError('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const progress = ((step - 1) / STEPS.length) * 100;

  if (exited) return <ExitScreen />;
  if (submitted) return <ThankYou />;

  return (
    <div style={s.root}>
      {/* Header */}
      <header style={s.header}>
        <Link to="/" style={s.logo}>datum<span style={s.logoAccent}>o</span>tiv</Link>
        <div style={s.progressWrap}>
          <div style={{ ...s.progressBar, width: `${progress}%` }} />
        </div>
        <span style={s.stepCounter}>{step} / {STEPS.length}</span>
      </header>

      {/* Survey card */}
      <main style={s.main}>
        {step === 1 && <Step1 form={form} set={set} />}
        {step === 2 && <Step2 form={form} set={set} />}
        {step === 3 && <Step3 form={form} set={set} toggleCheckbox={toggleCheckbox} />}
        {step === 4 && <Step4 form={form} set={set} toggleCheckbox={toggleCheckbox} />}
        {step === 5 && <Step5 form={form} set={set} />}

        {/* Honeypot — hidden from real users, bots fill it */}
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }} aria-hidden="true">
          <input
            type="text"
            name="website"
            value={form._hp}
            onChange={e => set('_hp', e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {error && <p style={s.errorMsg}>{error}</p>}

        <div style={s.btnRow}>
          {step > 1 && (
            <button style={s.btnBack} onClick={back}>← Back</button>
          )}
          {step < STEPS.length ? (
            <button style={s.btnNext} onClick={next}>Continue →</button>
          ) : (
            <button style={s.btnNext} onClick={submit} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit →'}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

/* ── Step components ── */

function StepHeader({ title, sub }) {
  return (
    <div style={s.stepHeader}>
      <h2 style={s.stepTitle}>{title}</h2>
      {sub && <p style={s.stepSub}>{sub}</p>}
    </div>
  );
}

function RadioGroup({ name, options, value, onChange }) {
  return (
    <div style={s.radioGroup}>
      {options.map(opt => {
        const val = typeof opt === 'string' ? opt : (opt.key ?? opt.value);
        const label = typeof opt === 'string' ? opt : opt.label;
        const checked = value === val;
        return (
          <label key={val} style={{ ...s.radioOption, ...(checked ? s.radioChecked : {}) }}>
            <input type="radio" name={name} value={val} checked={checked}
              onChange={() => onChange(val)} style={{ display: 'none' }} />
            <span style={{ ...s.radioDot, ...(checked ? s.radioDotChecked : {}) }} />
            <span style={s.radioLabel}>{label}</span>
          </label>
        );
      })}
    </div>
  );
}

function CheckboxGroup({ name, options, values, onChange }) {
  return (
    <div style={s.radioGroup}>
      {options.map(opt => {
        const val = typeof opt === 'string' ? opt : (opt.key ?? opt.value);
        const label = typeof opt === 'string' ? opt : opt.label;
        const checked = values.includes(val);
        return (
          <label key={val} style={{ ...s.radioOption, ...(checked ? s.radioChecked : {}) }}>
            <input type="checkbox" name={name} value={val} checked={checked}
              onChange={() => onChange(val)} style={{ display: 'none' }} />
            <span style={{ ...s.checkDot, ...(checked ? s.checkDotChecked : {}) }}>
              {checked && <span style={s.checkMark}>✓</span>}
            </span>
            <span style={s.radioLabel}>{label}</span>
          </label>
        );
      })}
    </div>
  );
}

function Q({ label, required, children }) {
  return (
    <div style={s.question}>
      <label style={s.qLabel}>{label}{required && <span style={s.req}> *</span>}</label>
      {children}
    </div>
  );
}

function Step1({ form, set }) {
  return (
    <>
      <StepHeader title="Tell us about your experience" sub="4 minutes. Anonymous. No spam." />
      <Q label="Do you currently own a petrol or diesel car in India?" required>
        <RadioGroup name="q1" value={form.q1} onChange={v => set('q1', v)}
          options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} />
      </Q>
      <Q label="Which city are you in?" required>
        <input style={s.textInput} value={form.q2} onChange={e => set('q2', e.target.value)}
          placeholder="e.g. Bangalore" />
      </Q>
      <Q label="What is your car's make and model?" required>
        <input style={s.textInput} value={form.q3} onChange={e => set('q3', e.target.value)}
          placeholder="e.g. Maruti Swift" />
      </Q>
      <Q label="What year did you buy this car?" required>
        <input style={s.textInput} value={form.q_own} onChange={e => set('q_own', e.target.value)}
          placeholder="e.g. 2019" />
      </Q>
      <Q label="What is the approximate current odometer reading?" required>
        <input style={s.textInput} value={form.q4} onChange={e => set('q4', e.target.value)}
          placeholder="e.g. 45,000 km" />
      </Q>
    </>
  );
}

function Step2({ form, set }) {
  return (
    <>
      <StepHeader title="Your maintenance experience" />
      <Q label="How would you describe yourself as a car owner?" required>
        <RadioGroup name="q_mindset" value={form.q_mindset} onChange={v => set('q_mindset', v)}
          options={Q_MINDSET_OPTIONS} />
      </Q>
      <Q label="In the last 12 months, how much did you spend on car maintenance and repairs (excluding fuel)?" required>
        <RadioGroup name="q5" value={form.q5} onChange={v => set('q5', v)}
          options={Q5_OPTIONS} />
      </Q>
      <Q label="Across all the cars you have owned, how often have repair bills come in higher than expected?" required>
        <RadioGroup name="q6" value={form.q6} onChange={v => set('q6', v)}
          options={Q6_OPTIONS} />
      </Q>
      <Q label="Think about the most significant unexpected repair bill you have had — with any car you have owned. Did you see it coming?" required>
        <RadioGroup name="q7" value={form.q7} onChange={v => set('q7', v)}
          options={Q7_OPTIONS} />
      </Q>
      <Q label="When dealing with mechanics, how often have you felt you fully understood what was done and why — across any car you have owned?" required>
        <RadioGroup name="q8" value={form.q8} onChange={v => set('q8', v)}
          options={Q8_OPTIONS} />
      </Q>
      <Q label="Have you ever suspected a mechanic recommended work that wasn't actually needed — across any car you have owned?" required>
        <RadioGroup name="q9" value={form.q9} onChange={v => set('q9', v)}
          options={Q9_OPTIONS} />
      </Q>
    </>
  );
}

function Step3({ form, set, toggleCheckbox }) {
  return (
    <>
      <StepHeader title="How you handle your car" />
      <Q label="How do you decide when to service your car? (Select all that apply)" required>
        <CheckboxGroup name="q10" values={form.q10}
          onChange={v => toggleCheckbox('q10', v, null)}
          options={Q10_OPTIONS} />
      </Q>
      <Q label="When something feels off with your car, what do you typically do first?" required>
        <RadioGroup name="q11" value={form.q11} onChange={v => set('q11', v)}
          options={Q11_OPTIONS} />
      </Q>
      <Q label="Have you ever experienced a breakdown (other than tyre puncture) that left you stranded requiring calling mechanic or required towing?" required>
        <RadioGroup name="q12" value={form.q12} onChange={v => set('q12', v)}
          options={Q12_OPTIONS} />
      </Q>
      <Q label="What is your single biggest frustration about owning and maintaining a car?">
        <textarea style={s.textarea} value={form.q13} onChange={e => set('q13', e.target.value)}
          placeholder="Tell us in your own words…" rows={4} />
      </Q>
    </>
  );
}

function Step4({ form, set, toggleCheckbox }) {
  return (
    <>
      <StepHeader title="What would make ownership better" />
      <Q label="When it comes to your car, which of these would matter most to you?" required>
        <RadioGroup name="q14" value={form.q14[0] || ''} onChange={v => set('q14', [v])}
          options={Q14_OPTIONS} />
      </Q>
      <Q label="Imagine you could get a heads-up 2–3 weeks before a part in your car was likely to fail. How much would avoiding that surprise repair be worth to you annually?" required>
        <RadioGroup name="q15" value={form.q15} onChange={v => set('q15', v)}
          options={Q15_OPTIONS} />
      </Q>
      <Q label="Do you currently use any tool, app, or service to monitor or track your car's health?" required>
        <RadioGroup name="q16" value={form.q16} onChange={v => set('q16', v)}
          options={Q16_OPTIONS} />
      </Q>
      {form.q16 === 'tool_yes' && (
        <Q label="What do you use — and are you happy with it?">
          <textarea style={s.textarea} value={form.q17} onChange={e => set('q17', e.target.value)}
            placeholder="Name of tool / app and your experience…" rows={3} />
        </Q>
      )}
      <Q label="If a solution existed that completely solved your biggest car ownership frustration, how would you prefer to find out about it? (Select all that apply)">
        <CheckboxGroup name="q18" values={form.q18}
          onChange={v => toggleCheckbox('q18', v, null)}
          options={Q18_OPTIONS} />
      </Q>
    </>
  );
}

function Step5({ form, set }) {
  return (
    <>
      <StepHeader title="A little about you" sub="Almost done." />
      <Q label="Your age" required>
        <RadioGroup name="q19" value={form.q19} onChange={v => set('q19', v)}
          options={Q19_OPTIONS} />
      </Q>
      <Q label="Your gender" required>
        <RadioGroup name="q20" value={form.q20} onChange={v => set('q20', v)}
          options={Q20_OPTIONS} />
      </Q>
      <Q label="Your profession">
        <input style={s.textInput} value={form.q21} onChange={e => set('q21', e.target.value)}
          placeholder="e.g. Software engineer" />
      </Q>
      <Q label="Your highest education level" required>
        <RadioGroup name="q22" value={form.q22} onChange={v => set('q22', v)}
          options={Q22_OPTIONS} />
      </Q>
      <Q label="Any other thoughts? Leave your contact if you'd like early access.">
        <textarea style={s.textarea} value={form.q23} onChange={e => set('q23', e.target.value)}
          placeholder="Optional — thoughts, feedback, or your email/WhatsApp…" rows={4} />
      </Q>
    </>
  );
}

function ExitScreen() {
  return (
    <div style={s.fullScreen}>
      <div style={s.centred}>
        <span style={s.logo2}>datum<span style={s.logoAccent}>o</span>tiv</span>
        <h2 style={s.thankTitle}>Thank you for stopping by.</h2>
        <p style={s.thankSub}>This survey is for current car owners in India. We hope to be useful to you soon.</p>
        <Link to="/" style={s.backLink}>← Back to datumotiv.com</Link>
      </div>
    </div>
  );
}

function ThankYou() {
  return (
    <div style={s.fullScreen}>
      <div style={s.centred}>
        <span style={s.logo2}>datum<span style={s.logoAccent}>o</span>tiv</span>
        <h2 style={s.thankTitle}>Thank you.</h2>
        <p style={s.thankSub}>Your input shapes what we build. We'll be in touch if you left your contact.</p>
        <Link to="/" style={s.backLink}>← Back to datumotiv.com</Link>
      </div>
    </div>
  );
}

/* ── Styles ── */
const s = {
  root: {
    minHeight: '100vh',
    background: '#faf8f5',
    fontFamily: "'DM Sans', sans-serif",
    color: '#1a1a1f',
  },
  header: {
    position: 'fixed',
    top: 0, left: 0, right: 0,
    padding: '18px 32px',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    background: '#faf8f5',
    borderBottom: '1px solid #ede8df',
    zIndex: 10,
  },
  logo: {
    fontFamily: "'Syne', sans-serif",
    fontWeight: 700,
    fontSize: 16,
    letterSpacing: '-0.02em',
    color: '#1a1a1f',
    textDecoration: 'none',
    flexShrink: 0,
  },
  logoAccent: { color: '#c9a84c' },
  progressWrap: {
    flex: 1,
    height: 3,
    background: '#e8e4de',
    borderRadius: 2,
  },
  progressBar: {
    height: '100%',
    background: '#c9a84c',
    borderRadius: 2,
    transition: 'width 0.4s ease',
  },
  stepCounter: {
    fontSize: 12,
    color: '#9a9488',
    flexShrink: 0,
    letterSpacing: '0.04em',
  },
  main: {
    maxWidth: 640,
    margin: '0 auto',
    padding: '96px 24px 120px',
  },
  stepHeader: {
    marginBottom: 40,
  },
  stepTitle: {
    fontFamily: "'Instrument Serif', serif",
    fontStyle: 'italic',
    fontSize: 'clamp(26px, 4vw, 36px)',
    fontWeight: 400,
    letterSpacing: '-0.02em',
    color: '#1a1a1f',
    marginBottom: 8,
  },
  stepSub: {
    fontSize: 14,
    color: '#9a9488',
  },
  question: {
    marginBottom: 36,
  },
  qLabel: {
    display: 'block',
    fontSize: 15,
    fontWeight: 500,
    color: '#1a1a1f',
    marginBottom: 14,
    lineHeight: 1.5,
    letterSpacing: '-0.01em',
  },
  req: { color: '#c9a84c' },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  radioOption: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 16px',
    border: '1px solid #e8e4de',
    borderRadius: 4,
    cursor: 'pointer',
    background: '#fff',
    transition: 'border-color 0.15s, background 0.15s',
  },
  radioChecked: {
    borderColor: '#c9a84c',
    background: 'rgba(201,168,76,0.04)',
  },
  radioDot: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: '2px solid #ccc8c0',
    flexShrink: 0,
    marginTop: 2,
    transition: 'border-color 0.15s',
  },
  radioDotChecked: {
    border: '5px solid #c9a84c',
  },
  checkDot: {
    width: 16,
    height: 16,
    borderRadius: 3,
    border: '2px solid #ccc8c0',
    flexShrink: 0,
    marginTop: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'border-color 0.15s, background 0.15s',
  },
  checkDotChecked: {
    border: '2px solid #c9a84c',
    background: '#c9a84c',
  },
  checkMark: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1,
  },
  radioLabel: {
    fontSize: 14,
    color: '#3a3a3f',
    lineHeight: 1.5,
  },
  textInput: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #e8e4de',
    borderRadius: 4,
    background: '#fff',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    color: '#1a1a1f',
    outline: 'none',
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #e8e4de',
    borderRadius: 4,
    background: '#fff',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    color: '#1a1a1f',
    outline: 'none',
    resize: 'vertical',
    lineHeight: 1.6,
  },
  limitNote: {
    fontSize: 12,
    color: '#c9a84c',
    marginTop: 8,
  },
  errorMsg: {
    fontSize: 13,
    color: '#c0604a',
    marginBottom: 16,
    padding: '10px 14px',
    background: 'rgba(192,96,74,0.06)',
    borderRadius: 4,
    border: '1px solid rgba(192,96,74,0.15)',
  },
  btnRow: {
    display: 'flex',
    gap: 12,
    marginTop: 12,
  },
  btnBack: {
    padding: '13px 24px',
    background: 'transparent',
    border: '1px solid #e8e4de',
    borderRadius: 4,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    color: '#9a9488',
    cursor: 'pointer',
  },
  btnNext: {
    flex: 1,
    padding: '13px 24px',
    background: '#1a1a1f',
    border: 'none',
    borderRadius: 4,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 15,
    fontWeight: 500,
    color: '#faf8f5',
    cursor: 'pointer',
    letterSpacing: '-0.01em',
  },
  fullScreen: {
    minHeight: '100vh',
    background: '#090910',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  centred: {
    textAlign: 'center',
    maxWidth: 480,
  },
  logo2: {
    fontFamily: "'Syne', sans-serif",
    fontWeight: 700,
    fontSize: 18,
    letterSpacing: '-0.02em',
    color: '#ede8df',
    display: 'block',
    marginBottom: 40,
  },
  thankTitle: {
    fontFamily: "'Instrument Serif', serif",
    fontStyle: 'italic',
    fontSize: 'clamp(36px, 6vw, 56px)',
    fontWeight: 400,
    color: '#ede8df',
    marginBottom: 16,
    letterSpacing: '-0.02em',
  },
  thankSub: {
    fontSize: 16,
    color: '#9a9488',
    lineHeight: 1.7,
    marginBottom: 40,
  },
  backLink: {
    fontSize: 13,
    color: '#c9a84c',
    textDecoration: 'none',
    letterSpacing: '0.02em',
  },
};
